import { useElementSize } from '@talos/kyoko';
import { clamp } from 'lodash-es';
import { useCallback, useEffect, useRef } from 'react';
import {
  MOBILE_SYMBOL_DEEP_DIVE_MODULES,
  useMobileSymbolDeepDiveModuleContext,
  type MobileSymbolDeepDiveModuleIndex,
} from './MobileSymbolDeepDiveModuleContext';
import { MobileHistoricalPriceChartModule } from './modules/MobileHistoricalPriceChartModule';
import { MobileOrderBookModule } from './modules/MobileOrderBookModule';
import { MobilePriceLadderModule } from './modules/MobilePriceLadderModule';
import { MobileVolumeLadderModule } from './modules/MobileVolumeLadderModule';
import { ModuleScrollContainer, ModuleSection, ModuleViewport } from './styles';

const MODULES = [
  { ...MOBILE_SYMBOL_DEEP_DIVE_MODULES[0], component: <MobileOrderBookModule /> },
  { ...MOBILE_SYMBOL_DEEP_DIVE_MODULES[1], component: <MobileVolumeLadderModule /> },
  { ...MOBILE_SYMBOL_DEEP_DIVE_MODULES[2], component: <MobileHistoricalPriceChartModule /> },
  { ...MOBILE_SYMBOL_DEEP_DIVE_MODULES[3], component: <MobilePriceLadderModule /> },
] as const;

export function MobileSymbolDeepDiveContent() {
  const { moduleIndex, setModuleIndex } = useMobileSymbolDeepDiveModuleContext();
  const scrollRef = useRef<HTMLDivElement>(null);
  const isSyncingScrollRef = useRef(false);
  const { elementRef: viewportRef, size } = useElementSize<HTMLDivElement>();
  const slideWidth = size.clientWidth ?? 0;

  useEffect(() => {
    const container = scrollRef.current;
    if (container == null || slideWidth === 0) {
      return;
    }

    const targetScrollLeft = moduleIndex * slideWidth;
    if (Math.abs(container.scrollLeft - targetScrollLeft) < 1) {
      return;
    }

    isSyncingScrollRef.current = true;
    container.scrollTo({ left: targetScrollLeft, behavior: 'smooth' });
  }, [moduleIndex, slideWidth]);

  const syncModuleIndexFromScroll = useCallback(() => {
    const container = scrollRef.current;
    if (container == null || isSyncingScrollRef.current || slideWidth === 0) {
      return;
    }

    const nextIndex = Math.round(container.scrollLeft / slideWidth);
    const clampedIndex = clamp(nextIndex, 0, MODULES.length - 1);

    if (clampedIndex !== moduleIndex) {
      setModuleIndex(clampedIndex as MobileSymbolDeepDiveModuleIndex);
    }
  }, [moduleIndex, setModuleIndex, slideWidth]);

  const handleScrollEnd = useCallback(() => {
    isSyncingScrollRef.current = false;
    syncModuleIndexFromScroll();
  }, [syncModuleIndexFromScroll]);

  useEffect(() => {
    const container = scrollRef.current;
    if (container == null) {
      return;
    }

    let rafId = 0;
    const handleScroll = () => {
      cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(syncModuleIndexFromScroll);
    };

    container.addEventListener('scroll', handleScroll, { passive: true });
    container.addEventListener('scrollend', handleScrollEnd);
    return () => {
      cancelAnimationFrame(rafId);
      container.removeEventListener('scroll', handleScroll);
      container.removeEventListener('scrollend', handleScrollEnd);
    };
  }, [handleScrollEnd, syncModuleIndexFromScroll]);

  return (
    <ModuleViewport ref={viewportRef}>
      <ModuleScrollContainer ref={scrollRef}>
        {MODULES.map(module => (
          <ModuleSection
            key={module.id}
            slideWidth={slideWidth}
            data-testid={`mobile-symbol-deep-dive-module-${module.id}`}
          >
            {module.component}
          </ModuleSection>
        ))}
      </ModuleScrollContainer>
    </ModuleViewport>
  );
}
